import { useState } from "react";
import { motion } from "framer-motion";
import { Navigation } from "./Index/components/Navigation";
import { Footer } from "./Index/components/Footer";
import { useLanguage } from "@/contexts/LanguageContext";
import { translations } from "@/translations";
import { Sun, Camera, BookOpen, GraduationCap, Lightbulb, Users, MapPin, Calendar, CheckCircle, Backpack, CreditCard, HelpCircle, Palette, ArrowDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { OpenHouseBanner } from "@/components/OpenHouseBanner";
import PricingTable from "@/components/PricingTable";
import FAQAccordion from "@/components/FAQAccordion";
import SummerCampRegistrationForm from "@/components/SummerCampRegistrationForm";
import { OptimizedPicture } from "@/components/OptimizedPicture";
import { usePageMeta } from "@/hooks/usePageMeta";

const META: Record<string, { title: string; description: string }> = {
  es: {
    title: "Campamento de verano 2026 | Gándara International School",
    description: "Campamento de verano 2026 en Gondomar para niños de 3 a 12 años. Talleres temáticos, piscina y juego libre en Gándara.",
  },
  en: {
    title: "Summer Camp 2026 | Gándara International School",
    description: "Summer camp 2026 in Gondomar for children aged 3-12. Themed workshops, swimming pool and free play at Gándara.",
  },
  pt: {
    title: "Acampamento de Verão 2026 | Gándara International School",
    description: "Acampamento de verão 2026 em Gondomar para crianças de 3 a 12 anos. Oficinas temáticas, piscina e brincadeira livre na Gándara.",
  },
  fr: {
    title: "Camp d'Été 2026 | Gándara International School",
    description: "Camp d'été 2026 à Gondomar pour enfants de 3 à 12 ans. Ateliers thématiques, piscine et jeu libre à Gándara.",
  },
};

const SummerCamp = () => {
  const { language } = useLanguage();
  const t = translations[language].summerCamp;
  const [isBannerVisible, setIsBannerVisible] = useState(true);
  const meta = META[language] || META.es;
  usePageMeta({ title: meta.title, description: meta.description });

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <OpenHouseBanner onVisibilityChange={setIsBannerVisible} />

      <div className={`${isBannerVisible ? 'pt-[120px]' : 'pt-20'} pb-20`}>
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-4xl mx-auto"
          >
            {/* Header */}
            <div className="text-center mb-12">
              <Sun className="w-16 h-16 text-primary mx-auto mb-6" />
              <h1 className="text-5xl font-light text-primary mb-6">
                {t.title}
              </h1>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
                {t.subtitle}
              </p>
              <Button
                size="lg"
                className="bg-primary hover:bg-primary/90 text-primary-foreground text-lg px-8 py-6"
                onClick={() => document.getElementById('registration')?.scrollIntoView({ behavior: 'smooth' })}
              >
                {t.registration.cta}
                <ArrowDown className="w-5 h-5 ml-2" />
              </Button>
            </div>

            {/* Gallery Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-card rounded-2xl p-8 shadow-lg mb-8 overflow-hidden"
            >
              <h2 className="text-3xl font-light text-primary mb-6 flex items-center gap-3">
                <Camera className="w-8 h-8 text-primary" />
                {t.gallery.title}
              </h2>

              <div className="relative aspect-video rounded-xl overflow-hidden mb-4 bg-muted">
                <OptimizedPicture
                  src={t.gallery.images[0].src}
                  alt={t.gallery.images[0].alt}
                  width={800}
                  height={450}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover transition-opacity duration-300"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="aspect-[4/3] rounded-lg overflow-hidden bg-muted">
                  <OptimizedPicture
                    src={t.gallery.images[1].src}
                    alt={t.gallery.images[1].alt}
                    width={400}
                    height={300}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover transition-opacity duration-300"
                  />
                </div>
                <div className="aspect-[4/3] rounded-lg overflow-hidden bg-muted">
                  <OptimizedPicture
                    src={t.gallery.images[2].src}
                    alt={t.gallery.images[2].alt}
                    width={400}
                    height={300}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover transition-opacity duration-300"
                  />
                </div>
              </div>

              <p className="text-sm text-muted-foreground text-center mt-4">
                {t.gallery.caption}
              </p>
            </motion.div>

            {/* Introduction Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-card rounded-2xl p-8 shadow-lg mb-8"
            >
              <h2 className="text-3xl font-light text-primary mb-6 flex items-center gap-3">
                <BookOpen className="w-8 h-8 text-primary" />
                {language === 'es' ? 'Introducción' : language === 'pt' ? 'Introdução' : language === 'fr' ? 'Introduction' : 'Introduction'}
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                {t.intro}
              </p>
            </motion.div>

            {/* Educational Proposal Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
              className="bg-card rounded-2xl p-8 shadow-lg mb-8"
            >
              <h2 className="text-3xl font-light text-primary mb-6 flex items-center gap-3">
                <GraduationCap className="w-8 h-8 text-primary" />
                {t.educationalProposal.title}
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-4">{t.educationalProposal.content}</p>
              <ul className="space-y-3 mb-6">
                {t.educationalProposal.items.map((item: string, index: number) => (
                  <li key={index} className="flex items-start gap-3">
                    <span className="text-primary mt-1">✓</span>
                    <span className="text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-muted-foreground leading-relaxed mb-4">{t.educationalProposal.additional}</p>
              <ul className="space-y-3 mb-4">
                {t.educationalProposal.methodologies.map((methodology: string, index: number) => (
                  <li key={index} className="flex items-start gap-3">
                    <span className="text-primary mt-1">✓</span>
                    <span className="text-muted-foreground">{methodology}</span>
                  </li>
                ))}
              </ul>
              <p className="text-muted-foreground leading-relaxed italic">{t.educationalProposal.conclusion}</p>
            </motion.div>

            {/* Weekly Workshops Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.35 }}
              viewport={{ once: true }}
              className="bg-card rounded-2xl p-8 shadow-lg mb-8"
            >
              <h2 className="text-3xl font-light text-primary mb-6 flex items-center gap-3">
                <Palette className="w-8 h-8 text-primary" />
                {t.educationalProposal.workshopsTitle}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                {t.educationalProposal.workshops.map((workshop: { day: string; activity: string }, index: number) => (
                  <div key={index} className="bg-muted rounded-lg p-4 text-center">
                    <p className="font-medium text-foreground mb-1">{workshop.day}</p>
                    <p className="text-muted-foreground text-sm">{workshop.activity}</p>
                  </div>
                ))}
              </div>
              <p className="text-sm italic text-muted-foreground">{t.educationalProposal.workshopsNote}</p>
            </motion.div>

            {/* Pedagogical Principles Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              viewport={{ once: true }}
              className="bg-card rounded-2xl p-8 shadow-lg mb-8"
            >
              <h2 className="text-3xl font-light text-primary mb-6 flex items-center gap-3">
                <Lightbulb className="w-8 h-8 text-primary" />
                {t.principles.title}
              </h2>
              <ol className="space-y-3">
                {t.principles.list.map((principle: string, index: number) => (
                  <li key={index} className="flex items-start gap-3">
                    <span className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5 text-sm">
                      {index + 1}
                    </span>
                    <span className="text-muted-foreground">{principle}</span>
                  </li>
                ))}
              </ol>
            </motion.div>

            {/* Audience Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              viewport={{ once: true }}
              className="bg-card rounded-2xl p-8 shadow-lg mb-8"
            >
              <h2 className="text-3xl font-light text-primary mb-6 flex items-center gap-3">
                <Users className="w-8 h-8 text-primary" />
                {t.audience.title}
              </h2>
              <p className="text-xl font-medium mb-2 text-foreground">{t.audience.ageGroups}</p>
              <div className="bg-muted rounded-lg p-6 text-center mb-6">
                <p className="text-lg font-medium text-foreground">{t.audience.ratio}</p>
              </div>
              <p className="text-muted-foreground">{t.audience.note}</p>
            </motion.div>

            {/* Location Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.6 }}
              viewport={{ once: true }}
              className="bg-card rounded-2xl p-8 shadow-lg mb-8"
            >
              <h2 className="text-3xl font-light text-primary mb-6 flex items-center gap-3">
                <MapPin className="w-8 h-8 text-primary" />
                {t.location.title}
              </h2>
              <h3 className="text-xl font-medium mb-2 text-foreground">{t.location.name}</h3>
              <p className="text-muted-foreground mb-4">{t.location.address}</p>
              <p className="text-muted-foreground mb-4">{t.location.description}</p>
              <ul className="space-y-3">
                {t.location.features.map((feature: string, index: number) => (
                  <li key={index} className="flex items-start gap-3">
                    <span className="text-primary mt-1">✓</span>
                    <span className="text-muted-foreground">{feature}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Schedule Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.7 }}
              viewport={{ once: true }}
              className="bg-card rounded-2xl p-8 shadow-lg mb-8"
            >
              <h2 className="text-3xl font-light text-primary mb-6 flex items-center gap-3">
                <Calendar className="w-8 h-8 text-primary" />
                {t.schedule.title}
              </h2>
              <p className="text-lg mb-2 text-foreground">{t.schedule.dates}</p>
              <p className="text-muted-foreground mb-2">{t.schedule.days}</p>
              <p className="text-sm text-muted-foreground mb-6 italic">{t.schedule.holidays}</p>
              <h3 className="text-xl font-medium mb-4 text-foreground">{t.schedule.timetable.title}</h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <tbody>
                    {t.schedule.timetable.slots.map((slot: { time: string; activity: string }, index: number) => (
                      <tr key={index} className={index % 2 === 0 ? "bg-muted" : ""}>
                        <td className="py-2 px-4 font-medium whitespace-nowrap text-foreground">{slot.time}</td>
                        <td className="py-2 px-4 text-muted-foreground">{slot.activity}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>

            {/* What's Included Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.8 }}
              viewport={{ once: true }}
              className="bg-card rounded-2xl p-8 shadow-lg mb-8"
            >
              <h2 className="text-3xl font-light text-primary mb-6 flex items-center gap-3">
                <CheckCircle className="w-8 h-8 text-primary" />
                {t.included.title}
              </h2>
              <ul className="space-y-3">
                {t.included.items.map((item: string, index: number) => (
                  <li key={index} className="flex items-start gap-3">
                    <span className="text-primary mt-1">✓</span>
                    <span className="text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* What to Bring Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.9 }}
              viewport={{ once: true }}
              className="bg-card rounded-2xl p-8 shadow-lg mb-8"
            >
              <h2 className="text-3xl font-light text-primary mb-6 flex items-center gap-3">
                <Backpack className="w-8 h-8 text-primary" />
                {t.toBring.title}
              </h2>
              <ul className="space-y-3 mb-6">
                {t.toBring.items.map((item: string, index: number) => (
                  <li key={index} className="flex items-start gap-3">
                    <span className="text-primary mt-1">•</span>
                    <span className="text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-sm italic text-muted-foreground">{t.toBring.note}</p>
            </motion.div>

            {/* Pricing Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 1.0 }}
              viewport={{ once: true }}
              className="bg-card rounded-2xl p-8 shadow-lg mb-8"
            >
              <h2 className="text-3xl font-light text-primary mb-6 flex items-center gap-3">
                <CreditCard className="w-8 h-8 text-primary" />
                {t.pricing.title}
              </h2>
              <PricingTable />
            </motion.div>

            {/* FAQ Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 1.1 }}
              viewport={{ once: true }}
              className="bg-card rounded-2xl p-8 shadow-lg mb-8"
            >
              <h2 className="text-3xl font-light text-primary mb-6 flex items-center gap-3">
                <HelpCircle className="w-8 h-8 text-primary" />
                {t.faq.title}
              </h2>
              <FAQAccordion />
            </motion.div>

            {/* Registration Form Card */}
            <motion.div
              id="registration"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 1.2 }}
              viewport={{ once: true }}
              className="bg-card rounded-2xl p-8 shadow-lg mb-8 scroll-mt-24"
            >
              <h2 className="text-2xl font-light text-primary mb-2">
                {t.registration.title}
              </h2>
              <p className="text-lg font-medium mb-2 text-foreground">
                {t.registration.limitedSpots}
              </p>
              <p className="text-muted-foreground mb-6">
                {t.registration.options}
              </p>
              <SummerCampRegistrationForm />
            </motion.div>
          </motion.div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default SummerCamp;
